import React, { useEffect, useRef, useState } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useMutation } from '@apollo/client';
import { ADD_REACTION } from '../../graphql/operations';
import {
  REACTION_OPTIONS,
  REACTION_TOKEN_SET,
  normalizeReactionToken
} from './reactionEmojiMap';

/**
 * ReactionPicker — curated quick reactions + emoji-mart picker.
 */
function ReactionPicker({ messageId, conversationId, onSelect, onClose }) {
  const [addReaction] = useMutation(ADD_REACTION);
  const [notice, setNotice] = useState('');
  const containerRef = useRef(null);

  // Move focus into the popover on mount and wire Esc to close. Without
  // this, keyboard users land the focus outside the dialog after clicking
  // the "+" trigger and have no way to dismiss it.
  useEffect(() => {
    const firstButton = containerRef.current?.querySelector('.reaction-picker-btn');
    if (firstButton && typeof firstButton.focus === 'function') {
      firstButton.focus();
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && typeof onClose === 'function') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSelect = async (emojiToken) => {
    try {
      await addReaction({
        variables: { messageId, conversationId, emoji: emojiToken }
      });
      setNotice('');
      if (onSelect) onSelect(emojiToken);
    } catch (err) {
      console.error('Failed to add reaction:', err);
    }
  };

  const handleQuickSelect = async (emojiToken) => {
    await handleSelect(emojiToken);
    if (onClose) onClose();
  };

  const handlePickerSelect = async (selection) => {
    const token = normalizeReactionToken(selection?.native || '');
    if (!REACTION_TOKEN_SET.has(token)) {
      setNotice('This reaction is not supported yet.');
      return;
    }
    await handleQuickSelect(token);
  };

  return (
    <div className="reaction-picker" role="dialog" aria-label="Pick a reaction" ref={containerRef}>
      <div className="reaction-picker-quick" role="listbox" aria-label="Quick reactions">
        {REACTION_OPTIONS.slice(0, 12).map(({ token, emoji, label }) => (
          <button
            key={token}
            type="button"
            className="reaction-picker-btn"
            title={label}
            aria-label={label}
            onClick={() => handleQuickSelect(token)}
          >
            {emoji}
          </button>
        ))}
      </div>
      <div className="reaction-picker-library" data-testid="reaction-picker-library">
        <Picker
          data={data}
          onEmojiSelect={handlePickerSelect}
          previewPosition="none"
          skinTonePosition="none"
          theme="auto"
          navPosition="top"
          perLine={8}
          emojiSize={18}
          maxFrequentRows={1}
        />
      </div>
      {notice && (
        <div className="reaction-picker-note" role="status">
          {notice}
        </div>
      )}
    </div>
  );
}

export default ReactionPicker;
